// Module imports
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"

// CSS imports

// API imports
import AuthAPI from "../../../api/auth"

// Redux action imports
import {
  setAccessToken,
  setUserLogIn,
  setAuthState,
  setIsTermsChecked,
} from "../../redux/slices/appSlice"

// Atomic Design imports
import { AuthPageWrapper } from "./Atom"

// Component imports
import AuthBanner from "./AuthBanner"
import AuthMain from "./AuthMain"

// Helper imports
import { clearLocalstorage, setTokenLocal } from "../../../helper/utils"
import { useSelector } from "react-redux"
import { isValid } from "date-fns"
import { isValidEmail } from "../../../helper/validations/inputValidations"

// Types
type Props = {
  page: string
}
type LoginPayload = {
  email: string
  password: string
}

// Initialisers
const authAPI = new AuthAPI()

const AuthPage = (props: Props) => {
  const { page } = props
  const navigate = useNavigate()

  useEffect(() => {
    if (page == "auth") {
      navigate("/auth")
    }
  }, [])

  const currentUser = useSelector((state: any) => {
    return state.admin.currentUser
  })

  const [loading, setLoading] = useState<boolean>(false)
  const [emailError, setEmailError] = useState("")
  const [forgotEmailError, setForgotEmailError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [newPasswordError, setNewPasswordError] = useState("")
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState("")
  const [resetNewPasswordError, setResetNewPasswordError] = useState("")
  const [resetNewConfirmPasswordError, setResetNewConfirmPasswordError] =
    useState("")
  const [changeOldPasswordError, setChangeOldPasswordError] = useState("")
  const [changeNewPasswordError, setChangeNewPasswordError] = useState("")
  const [changeNewConfirmPasswordError, setChangeNewConfirmPasswordError] =
    useState("")

  const [email, setEmail] = useState("")
  const [forgotEmail, setForgotEmail] = useState("")

  const [resetNewPassword, setResetNewPassword] = useState("")
  const [changeOldPassword, setChangeOldPassword] = useState("")
  const [changeNewPassword, setChangeNewPassword] = useState("")
  const [resetNewConfirmPassword, setResetNewConfirmPassword] = useState("")
  const [changeNewConfirmPassword, setChangeNewConfirmPassword] = useState("")

  const [password, setPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")

  const [otp, setOtp] = useState("")
  const [resetOtp, setResetOtp] = useState("")

  useEffect(() => {
    if (page == "createPassword") {
      dispatch(setAuthState("createPassword"))
    } else if (page == "verifyEmail") {
      dispatch(setAuthState("verifyEmail"))
    } else if (page == "changePassword") {
      dispatch(setAuthState("changePassword"))
    } else if (page == "forgotPassword") {
      dispatch(setAuthState("forgotPassword"))
    } else if (page == "resetPassword") {
      dispatch(setAuthState("resetPassword"))
    } else {
      dispatch(setAuthState("login"))
    }
  }, [])

  const dispatch = useDispatch()

  const isUserActive = async (email: string): Promise<boolean> => {
    const response = await authAPI.getUserRole(email)

    if (response?.data?.data) {
      if (response?.data?.data?.status !== "inactive") return true
    } else {
      toast.error(response?.data?.display_message)
    }

    return false
  }

  // const adminLogin = async (payload: LoginPayload) => {
  //   const result = await authAPI.adminLogin(payload)
  //   return result
  // }

  // const changePassword = async (oldPassword: string, newPassword: string) => {
  //   try {
  //     const user = await Auth.currentAuthenticatedUser()
  //     const data = await Auth.changePassword(user, oldPassword, newPassword)
  //     console.log(data)
  //   } catch (err) {
  //     console.log(err)
  //   }
  // }

  const handleLogin = async (): Promise<void> => {
    setLoading(true)
    if (email == "") {
      setEmailError("Can't be empty")
    } else if (password == "") {
      setPasswordError("Can't be empty")
    } else {
      const isActive = await isUserActive(email)

      if (isActive) {
        try {
          const response: any = await authAPI.userLogin(
            email?.trim(), // email
            password?.trim(), // password
          )

          if (response?.data?.status_code === 402) {
            setLoading(false)
            navigate("/createPassword")
            return
          }

          if (response?.data?.status_code === 451) {
            setLoading(false)
            navigate("/verifyEmail")
            return
          }

          if (response?.data?.status_code === 200) {
            setTokenLocal(response?.data?.data?.accessToken)
            dispatch(setUserLogIn(true))
            toast.success(response?.data?.display_message)
            navigate("/")
          } else {
            toast.error(response?.data?.display_message)
          }
        } catch (error: any) {
          toast.error(JSON.stringify(error.message))
        }
      }
    }
    setLoading(false)
  }

  const handleCreatePassword = async () => {
    setLoading(true)
    if (newPassword == "") {
      setNewPasswordError("Can't be empty")
    } else if (confirmNewPassword == "") {
      setConfirmNewPasswordError("Can't be empty")
    } else if (newPassword == password) {
      setNewPasswordError("New password can not be same as previous password.")
    } else if (newPassword != confirmNewPassword) {
      setNewPasswordError("Passwords do not match")
      setConfirmNewPasswordError("Passwords do not match")
    } else {
      try {
        const response = await authAPI.userCreatePassword(
          email?.trim(), // email or username
          password?.trim(), // Admin Given Password
          newPassword?.trim(), // New Password
        )
        if (response?.data?.status_code === 200) {
          toast.success(response?.data?.display_message)
          toast.info("Please login again with your new password", {
            delay: 3000,
            autoClose: 5000,
          })
          dispatch(setIsTermsChecked(false))
          navigate("/auth")
        } else {
          toast.error(response?.data?.display_message)
        }
      } catch (err: any) {
        toast.error("Password could not be changed, please try again.")
      }
    }
    setLoading(false)
  }

  const handleVerifyEmail = async () => {
    setLoading(true)
    const response = await authAPI.userVerifyEmail(
      email?.trim(), // email
      password?.trim(), // password
      otp, // OTP sent to email
    )

    if (response?.data?.status_code == 200) {
      setTokenLocal(response?.data?.data?.accessToken)
      dispatch(setUserLogIn(true))
      toast.success(response?.data?.display_message)
      navigate("/")
    } else {
      toast.error(response?.data?.display_message)
    }
    setLoading(false)
  }

  const handleForgotPassword = async () => {
    setLoading(true)
    if (!isValidEmail(forgotEmail)) {
      setForgotEmailError("Invalid email!")
    } else {
      const response = await authAPI.userForgotPassword(forgotEmail)

      if (response?.data?.status_code == 200) {
        toast.success(response?.data?.display_message)
        navigate("/verifyResetPassword")
      } else {
        toast.error(response?.data?.display_message)
      }
    }
    setLoading(false)
  }

  const handleResetPasswordOtp = async () => {
    setLoading(true)
    setTimeout(() => {
      navigate("/resetPassword")
      setLoading(false)
    }, 3000)
  }

  const handleResetPassword = async () => {
    setLoading(true)
    if (resetNewPassword == "") {
      setResetNewPasswordError("Can't be empty")
    } else if (resetNewConfirmPassword == "") {
      setResetNewConfirmPasswordError("Can't be empty")
    }
    // else if (resetNewPassword == password) {
    //   setNewPasswordError("New password can not be same as previous password.")
    // }
    else if (resetNewPassword != resetNewConfirmPassword) {
      setResetNewPasswordError("Passwords do not match")
      setResetNewConfirmPasswordError("Passwords do not match")
    } else {
      const response = await authAPI.userResetPassword(
        forgotEmail,
        resetNewPassword,
        resetOtp,
      )

      if (response?.data?.status_code == 200) {
        toast.success(response?.data?.display_message)
        navigate("/auth")
      } else {
        toast.error(response?.data?.display_message)
      }
    }

    setLoading(false)
  }

  const handleChangePassword = async () => {
    setLoading(true)
    if (changeOldPassword == "") {
      setChangeOldPasswordError("Can't be empty")
    } else if (changeNewPassword == "") {
      setChangeNewPasswordError("Can't be empty")
    } else if (changeNewConfirmPassword == "") {
      setChangeNewConfirmPasswordError("Can't be empty")
    } else if (changeNewPassword == changeOldPassword) {
      setChangeNewPasswordError(
        "New password can not be same as previous password.",
      )
    } else if (changeNewPassword != changeNewConfirmPassword) {
      setChangeNewPasswordError("Passwords do not match")
      setChangeNewConfirmPasswordError("Passwords do not match")
    } else {
      const response = await authAPI.userChangePassword(
        currentUser?.email,
        changeOldPassword,
        changeNewPassword,
      )

      if (response?.data?.status_code == 200) {
        toast.success(response?.data?.display_message)
        clearLocalstorage()
        navigate("/auth")
      } else {
        toast.error(response?.data?.display_message)
      }
    }

    setLoading(false)
  }

  return (
    <>
      <AuthPageWrapper>
        <AuthBanner />
        <AuthMain
          loading={loading}
          page={page}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          emailError={emailError}
          passwordError={passwordError}
          handleLogin={handleLogin}
          setNewPassword={setNewPassword}
          setConfirmNewPassword={setConfirmNewPassword}
          newPasswordError={newPasswordError}
          confirmNewPasswordError={confirmNewPasswordError}
          handleCreatePassword={handleCreatePassword}
          forgotEmail={forgotEmail}
          otp={otp}
          setOtp={setOtp}
          handleVerifyEmail={handleVerifyEmail}
          setForgotEmail={setForgotEmail}
          forgotEmailError={forgotEmailError}
          handleForgotPassword={handleForgotPassword}
          resetOtp={resetOtp}
          setResetOtp={setResetOtp}
          handleResetPasswordOtp={handleResetPasswordOtp}
          setResetNewPassword={setResetNewPassword}
          setResetNewConfirmPassword={setResetNewConfirmPassword}
          resetNewPasswordError={resetNewPasswordError}
          resetNewConfirmPasswordError={resetNewConfirmPasswordError}
          handleResetPassword={handleResetPassword}
          setChangeOldPassword={setChangeOldPassword}
          setChangeNewPassword={setChangeNewPassword}
          setChangeNewConfirmPassword={setChangeNewConfirmPassword}
          changeOldPasswordError={changeOldPasswordError}
          changeNewPasswordError={changeNewPasswordError}
          changeNewConfirmPasswordError={changeNewConfirmPasswordError}
          handleChangePassword={handleChangePassword}
        />
      </AuthPageWrapper>
    </>
  )
}

export default AuthPage
